import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';

const VUEXY_COLORS = ['#7367f0', '#28c76f', '#ea5455', '#ff9f43', '#00cfe8', '#845adf'];

const ORDER_STATUS_LABEL = {
    1: 'قيد المراجعة',
    2: 'معتمد',
    3: 'مرفوض',
    4: 'مطلوب تعديل',
    5: 'تم التحويل للصرف',
};

const MOVEMENT_TYPE_LABEL = {
    1: 'خصم — طلب أدوية',
    2: 'صرف مستقل',
    3: 'إيداع — رصيد المرفق',
    4: 'صرف مباشر — خصم',
    5: 'صرف مباشر — استلام سليم',
    6: 'صرف مباشر — استلام تالف',
    7: 'Dispatch — خصم',
    8: 'Dispatch — استلام',
    11: 'صرف داخلي — خروج',
    12: 'صرف داخلي — دخول',
};

export default {
    name: 'MainDashboard',
    mixins: [HelperMixin],

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails) {
            this.logout();
            return;
        }
        // حساب مكتب تقنية المعلومات المتخصص: لا لوحة مؤشرات — وجهته شاشة المستخدمين
        if (this.isItOfficeOnlyUi()) {
            this.href('/Users');
            return;
        }
        await this.refreshAll();
    },

    beforeDestroy() {
        try { this.chartInstance && this.chartInstance.destroy(); } catch (_) { /* noop */ }
    },

    computed: {
        showFinancialQuickAccess() {
            return this.canViewFinancialUi();
        },
    },

    data() {
        return {
            ScreenTitle: 'لوحة التحكم',
            cardsResponse: null,
            chartsResponse: null,
            recent: { recentOrders: [], recentMovements: [], recentNotifications: [] },
            chartInstance: null,
            ministerData: null,
        };
    },

    filters: {
        moment(d) { return d ? moment(d).format('DD/MM/YYYY') : '—'; },
        momentTime(d) { return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—'; },
    },

    methods: {
        hasMinisterDashboardAccess() {
            return this.hasUiPermission('minister_dashboard.access');
        },

        fmtMoney(v) {
            if (v == null) return '—';
            const n = Number(v);
            if (!Number.isFinite(n)) return '—';
            return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
        },

        fmtNum(v) {
            if (v == null) return '0';
            const n = Number(v);
            if (!Number.isFinite(n)) return '0';
            return new Intl.NumberFormat('en-US').format(n);
        },

        formatCardValue(card) {
            if (!card) return '';
            if (card.isMoney) return this.fmtMoney(card.value);
            if (typeof card.value === 'number') return this.fmtNum(card.value);
            return card.value || '—';
        },

        cardClass(tone) {
            const map = {
                primary: 'tone-primary',
                success: 'tone-success',
                info: 'tone-info',
                warning: 'tone-warning',
                danger: 'tone-danger',
                secondary: 'tone-secondary',
            };
            return map[tone] || 'tone-primary';
        },

        orderStatusLabel(status) {
            return ORDER_STATUS_LABEL[Number(status)] || '—';
        },

        movementTypeLabel(t) {
            return MOVEMENT_TYPE_LABEL[Number(t)] || '—';
        },

        extractError(err) {
            if (err && err.response && err.response.data) {
                return typeof err.response.data === 'string' ? err.response.data : (err.response.data.message || 'حدث خطأ غير متوقع');
            }
            return (err && err.message) || 'حدث خطأ غير متوقع';
        },

        async refreshAll() {
            this.$blockUI.Start();
            try {
                if (this.hasMinisterDashboardAccess()) {
                    const res = await this.$http.GetMinisterDashboardData();
                    this.ministerData = (res && res.data) || null;
                } else {
                    const [c, ch, r] = await Promise.all([
                        this.$http.GetMainDashboardCards(),
                        this.$http.GetMainDashboardChartsData(),
                        this.$http.GetMainDashboardRecentActivities(),
                    ]);
                    this.cardsResponse = (c && c.data) || { cards: [] };
                    this.chartsResponse = (ch && ch.data) || null;
                    this.recent = (r && r.data) || { recentOrders: [], recentMovements: [], recentNotifications: [] };
                    this.$nextTick(() => this.renderChart());
                }
            } catch (err) {
                this.$helper.ShowMessage('error', 'تعذّر تحميل لوحة التحكم', this.extractError(err));
            } finally {
                this.$blockUI.Stop();
            }
        },

        renderChart() {
            try { this.chartInstance && this.chartInstance.destroy(); } catch (_) { /* noop */ }
            this.chartInstance = null;

            const c = this.chartsResponse;
            const el = document.getElementById('mainDashChart');
            if (!c || !c.chartType || c.chartType === 'none' || !el || typeof window.ApexCharts === 'undefined') return;
            const data = c.data || [];
            if (!data.length) return;

            if (c.chartType === 'line') {
                this.chartInstance = new window.ApexCharts(el, {
                    chart: { type: 'area', height: 280, toolbar: { show: false } },
                    series: [{ name: 'الكمية', data: data.map((x) => Number(x.value || 0)) }],
                    xaxis: { categories: data.map((x) => x.label) },
                    colors: [VUEXY_COLORS[0]],
                    dataLabels: { enabled: false },
                    stroke: { curve: 'smooth', width: 2 },
                });
                this.chartInstance.render();
            } else if (c.chartType === 'bar') {
                this.chartInstance = new window.ApexCharts(el, {
                    chart: { type: 'bar', height: 280, toolbar: { show: false } },
                    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
                    series: [{ name: 'الكمية', data: data.map((x) => Number(x.value || 0)) }],
                    xaxis: { categories: data.map((x) => x.label) },
                    colors: [VUEXY_COLORS[1]],
                    dataLabels: { enabled: false },
                });
                this.chartInstance.render();
            }
        },

        OpenOrder(item) {
            if (!item) return;
            this.href('/MedicineOrders');
        },
    },
};
